import { Bounds } from '@ardoq/graph';
import { BlocksGraph, BlocksNode } from '../types';
import { isGroup, isNodeOpen } from '../util';
import { rectCenter } from 'tabview/visualizations/misc/geometry';

/* these flip and rotate functions force the specified groups to manual layout. that's not
strictly speaking wrong, but a more subtle approach might be to set a group flag
which reinterprets the automatic layout if applicable */

export const flipGroupsHorizontal = (
  _graph: BlocksGraph,
  groups: BlocksNode[]
): void => {
  for (const group of groups) {
    if (isGroup(group) && group.colSize && group.rowSize) {
      const colMax = group.colSize!.length - 1;

      for (const child of group.children) {
        const col =
          colMax - (child.layoutData!.col + child.layoutData!.colSpan - 1);

        if (col !== child.layoutData!.col) {
          child.layoutData!.col = col;
        }
      }

      group.layoutType = 'manual';
    }
  }
};

export const flipGroupsVertical = (
  _graph: BlocksGraph,
  groups: BlocksNode[]
): void => {
  for (const group of groups) {
    if (isGroup(group) && group.colSize && group.rowSize) {
      const rowMax = group.rowSize!.length - 1;

      for (const child of group.children) {
        const row =
          rowMax - (child.layoutData!.row + child.layoutData!.rowSpan - 1);

        if (child.layoutData!.row !== row) {
          child.layoutData!.row = row;
        }
      }

      group.layoutType = 'manual';
    }
  }
};

export const rotateGroups = (
  _graph: BlocksGraph,
  groups: BlocksNode[]
): void => {
  for (const group of groups) {
    if (isGroup(group) && group.colSize && group.rowSize) {
      /* rotate the children */

      const colMax = group.colSize.length - 1;

      for (const child of group.children) {
        const left = child.layoutData!.row;
        const top =
          colMax - (child.layoutData!.col + child.layoutData!.colSpan - 1);

        if (
          child.layoutData!.col !== left ||
          child.layoutData!.row !== top ||
          child.layoutData!.colSpan !== child.layoutData!.rowSpan
        ) {
          child.layoutData = {
            col: left,
            row: top,
            colSpan: child.layoutData!.rowSpan,
            rowSpan: child.layoutData!.colSpan,
          };
        }
      }

      /* rotate the group */

      const colSize = group.colSize;
      group.colSize = group.rowSize;
      group.rowSize = colSize;

      group.layoutType = 'manual';
    }
  }
};

export const setGroupsExpandedAll = (
  graph: BlocksGraph,
  value: boolean
): boolean => {
  let update = false;

  const expandAll = (node: BlocksNode, value: Bounds | undefined) => {
    let cell = value;

    if (node.parent) {
      if (value) {
        node.cell = value;
      }

      if (isGroup(node) && !isNodeOpen(node)) {
        node.open = true;
        update = true;

        if (!cell && node.cell) {
          const center = rectCenter(node.cell);

          cell = [...center, ...center];
        }
      }
    }

    if (isGroup(node)) {
      for (const child of node.children) {
        expandAll(child, cell);
      }
    }
  };

  const collapseAll = (node: BlocksNode) => {
    if (node.parent) {
      // node.cell = undefined;

      if (isGroup(node) && isNodeOpen(node)) {
        node.open = false;
        update = true;
      }
    }

    if (isGroup(node)) {
      for (const child of node.children) {
        collapseAll(child);
      }
    }
  };

  if (value) {
    expandAll(graph.root, undefined);
  } else {
    collapseAll(graph.root);
  }

  return update;
};

export const setGroupsExpanded = (
  groups: BlocksNode[],
  value: boolean
): boolean => {
  let update = false;

  const setCells = (node: BlocksNode, cell: Bounds) => {
    node.cell = cell;

    if (node.children && isNodeOpen(node)) {
      for (const child of node.children) {
        setCells(child, cell);
      }
    }
  };

  for (const group of groups) {
    if (group.children && isNodeOpen(group) !== value) {
      group.open = value;

      if (isNodeOpen(group) && group.cell) {
        const center = rectCenter(group.cell);
        const cell: Bounds = [...center, ...center];

        for (const child of group.children) {
          setCells(child, cell);
        }
      }

      update = true;
    }
  }

  return update;
};
